import { z } from 'zod';

/* ------------------------------------------------------------------ GET /api/v1/auth/me -- */

const MeUserSchema = z.object({
  _id: z.string(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  full_name: z.string().nullish(),
  email: z.string().nullish(),
  phone_number: z.string().nullish(),
  profile_picture_url: z.string().nullish(),
  currency_id: z.string().nullish(),
  timezone: z.string().nullish(),
});

const MeRoleSchema = z.object({
  name: z.string().nullish(),
  permissions: z.array(z.string()).default([]),
});

const MeUserConfigSchema = z.object({
  default_currency_id: z.string().nullish(),
  // The user's "auto attach location" preference (mobile's `autoAttachLocation`) — gates the
  // New Bill auto-location fetch. Absent ⇒ treated as ON (only an explicit false disables).
  auto_attach_location: z.boolean().nullish(),
});

const MeDataSchema = z.object({
  user: MeUserSchema,
  role: MeRoleSchema.nullish(),
  user_config: MeUserConfigSchema.nullish(),
});

export const MeResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: MeDataSchema,
});

export type MeData = z.infer<typeof MeDataSchema>;

/* ----------------------------------------------------------------- POST /api/v1/auth/login -- */

/** User object inside the Riloma-API login `data` payload (only the fields we consume). */
export const BackendUserSchema = z.object({
  _id: z.string(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: z.string().nullish(),
  phone_number: z.string().nullish(),
  timezone: z.string().nullish(),
  profile_picture: z.string().nullish(),
  profile_picture_url: z.string().nullish(),
  // The user's preferred currency (`_id`) — used as the default on the New Bill screen.
  currency_id: z.string().nullish(),
  password_reset: z.boolean().optional(),
});

export const BackendRoleSchema = z.object({
  name: z.string().nullish(),
});

/** `data` of `POST /api/v1/auth/login`. The JWT is `token` (`access_token` accepted too). */
export const LoginDataSchema = z.object({
  token: z.string().optional(),
  access_token: z.string().optional(),
  user: BackendUserSchema,
  role: BackendRoleSchema.nullish(),
});

/** Full envelope: `{ status, message, data }`. */
export const LoginResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: LoginDataSchema,
});

export type LoginData = z.infer<typeof LoginDataSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
